import { memo } from "react";
import { selectVisibleTabularViewColumns } from "../../../Search/redux/searchSelectors";
import { useDispatch, useSelector } from "react-redux";
import { changeSort } from "../../../Search/redux/actions";
import { useHSATableConfig } from "./HousingTabularViewConfig";
import { selectHSATabularViewData } from "../../redux/selectors";
import { HousingMessages } from "../../../common/components/Messages/HousingMessages";
import { HSA_TABLE_CONTAINER_JS_CLASS } from "../VariantProductsTableModal/HousingTabularViewDetailPanel";
import { CONTAINER_DESKTOP_L, CONTAINER_DESKTOP_M, CONTAINER_DESKTOP_S } from "../../../utils/screenSizes";
import { calculateTotalTableWidth } from "~/Search/components/ProductResults/views/TabularView/TanStackColumnDefs";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { type TableState } from "@tanstack/react-table";
import { TableHoverHighlight } from "~/Search/components/ProductResults/views/TabularView/TableHoverHighlight";
import { useTranslation } from "react-i18next";
import { SortDirection } from "~/Search/models";
import { ResetSortBtn } from "~/BearingSelectionAssistant/components/Buttons/ResetSortBtn";
import classes from "~/BearingSelectionAssistant/components/SATabularView.module.less";
import { OpenAbbreviationsBtn } from "~/BearingSelectionAssistant/components/AbbreviationListModal/AbbreviationListModal";
import { HSA_ATTRIBUTES } from "~/BearingSelectionAssistant/components/AbbreviationListModal/constants";
import { SIZE_DESKTOP_L, SIZE_DESKTOP_S, SIZE_TABLET, useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";

interface Props {
  totalNumberOfResults: number;
  submitHandler: () => unknown;
}

export const HSAResultsTable = memo(({ totalNumberOfResults }: Props) => {
  const size = useNumericScreenSize();
  const isMinTablet = size >= SIZE_TABLET;
  const isDesktopS = size >= SIZE_DESKTOP_S;
  const isDesktopM = size >= SIZE_DESKTOP_S;
  const isDesktopL = size >= SIZE_DESKTOP_L;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const config = useSelector(selectVisibleTabularViewColumns);

  const tanStackColumns = useHSATableConfig(size);

  const totalTableWidth = calculateTotalTableWidth(tanStackColumns);
  let containerSize = 0;
  if (isDesktopL) {
    containerSize = CONTAINER_DESKTOP_L;
  } else if (isDesktopM) {
    containerSize = CONTAINER_DESKTOP_M;
  } else if (isDesktopS) {
    containerSize = CONTAINER_DESKTOP_S;
  }
  const isTableLargerThanDesktop = totalTableWidth > containerSize;

  let state: Partial<TableState> = {};
  let fullwidth = true;
  if (!isMinTablet) {
    state = { columnPinning: { left: ["name"] } };
    fullwidth = false;
  } else if (!isDesktopS || isTableLargerThanDesktop) {
    state = { columnPinning: { left: ["name"], right: ["actions"] } };
    fullwidth = false;
  } else if (!isTableLargerThanDesktop) {
    // SCHAEFF-26787
    // Give all excess free space to the name column
    tanStackColumns[0].size = undefined;
  }

  const results = useSelector(selectHSATabularViewData);

  if (!config) {
    return null;
  }

  // TODO FIXME USE ICON

  let table = (
    <TanStackTable
      key={size}
      variant={"product"}
      data={results}
      columns={tanStackColumns}
      {...{ fullwidth, state }}
      stickyHeader={true}
    />
  );

  if (isDesktopS) {
    table = <TableHoverHighlight excludeLastCol={true}>{table}</TableHoverHighlight>;
  }

  return (
    <div>
      <h2 data-test-id={"selectionassistant-resultstitle"} className="scha__sa__results__title">
        {t("page.selectionAssistant.productsHeader", {
          count: totalNumberOfResults,
        })}
      </h2>
      <HousingMessages.HSAUnits className={"scha__hsa__units-msg"} />

      <div className={classes.buttonList}>
        <ResetSortBtn onClick={() => dispatch(changeSort("name", SortDirection.ASC))} />

        {!isDesktopS && <OpenAbbreviationsBtn attributes={HSA_ATTRIBUTES} />}
      </div>

      <div className={`scha__hsa__result-table ${HSA_TABLE_CONTAINER_JS_CLASS}`}>{table}</div>
    </div>
  );
});
